// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack, useGlobalSearchParams, useRouter, useSegments } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Modal, Platform, Pressable, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';

import { useColorScheme } from '@/components/useColorScheme';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { HeaderUser } from '@/components/HeaderUser';
import { BottomMenu } from '@/components/BottomMenu';
import { getCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import { useDebugState } from '@/stores/debugState';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useAuthFlow } from '@/app/layout/useAuthFlow';
import { useServiceHealth } from '@/app/layout/useServiceHealth';
import { useUserInfoSync } from '@/app/layout/useUserInfoSync';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { ServiceStatusModal } from '@/app/layout/ServiceStatusModal';
import { DebugModal } from '@/app/layout/DebugModal';
import { createAppLogger } from '@/services/appLogger';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getSharedBffWebSocket, type BffInboundMessage } from '@/services/bffWebSocket';
import { getCoachMarkStatesForDebug, resetCoachMarksForDebug } from '@/services/coachMarks';
import appColors from '@/theme/colors';

type BugImageSelection = {
  file: File;
  fileName: string;
  contentType: string;
  size: number;
  previewUrl: string;
  uploadedUrl: string | null;
};

const COMPACT_WEB_HEADER_BREAKPOINT = 430;

export default function RootLayoutNav() {
  const colorScheme = useColorScheme();
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const { width } = useWindowDimensions();
  const router = useRouter();
  const segments = useSegments();
  const { debug: debugParam } = useGlobalSearchParams<{ debug?: string }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [bugReportDescription, setBugReportDescription] = useState('');
  const [bugReportImage, setBugReportImage] = useState<BugImageSelection | null>(null);
  const [bugReportSubmitting, setBugReportSubmitting] = useState(false);
  const [bugReportError, setBugReportError] = useState<string | null>(null);
  const faviconImage = useMemo(() => getAssetImageSource('images/login-hero.png', 'icon'), []);
  const [debugTourName, setDebugTourName] = useState<string | null>(null);
  const [debugRoundName, setDebugRoundName] = useState<string | null>(null);
  const [debugPlayerId, setDebugPlayerId] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugCoachMarks, setDebugCoachMarks] = useState<Array<{ key: string; shownCount: number; maxShows: number }>>([]);
  const [debugCoachMarksLoading, setDebugCoachMarksLoading] = useState(false);
  const topSegment = segments[0];
  const roundId = useRoundState((state) => state.roundId);
  const scorerOnlyMode = useRoundState((state) => state.scorerOnlyMode);
  const roundIdFromSegments = useMemo(() => {
    if (topSegment !== 'round') {
      return null;
    }
    const segmentValue = segments[1];
    return typeof segmentValue === 'string' && segmentValue.length > 0 ? segmentValue : null;
  }, [segments, topSegment]);
  const isHoleRoute = useMemo(() => {
    return topSegment === 'round' && segments[2] === 'hole';
  }, [segments, topSegment]);
  const effectiveRoundId = roundId ?? roundIdFromSegments;
  const debugEnabled = useDebugState((state) => state.enabled);
  const setDebugEnabled = useDebugState((state) => state.setEnabled);
  const [tourId, setTourId] = useState<string | null>(null);
  const [mysteryMenuEnabled, setMysteryMenuEnabled] = useState(false);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'frontend-error',
      }),
    [apiBaseUrl, user?.token]
  );
  const { handleRedirect } = useAuthRedirect({ user, setUser });

  const { authChecked } = useAuthFlow({ user, setUser });

  const {
    canCheckServiceHealth,
    serviceStatusOpen,
    setServiceStatusOpen,
    serviceStatusMissing,
    serviceStatusCheckedAt,
    serviceStatusHasWarnings,
    serviceStatusFetchFailed,
    serviceStatusFlashOn,
    serviceStatusWarnings,
  } = useServiceHealth({ apiBaseUrl, user });
  const isInviteRoute = segments[0] === 'tour' && segments[1] === 'invite';
  const isPublicInviteView = isInviteRoute && !user?.token;
  const isPublicLandingView = !user?.token && (segments.length === 0 || topSegment === 'index');
  const isAnonymousMode = !user?.token;
  const compactWebHeader = Platform.OS === 'web' && width > 0 && width <= COMPACT_WEB_HEADER_BREAKPOINT;

  useEffect(() => {
    const raw = Array.isArray(debugParam) ? debugParam[0] : debugParam;
    if (raw === 'true' || raw === '1') {
      setDebugEnabled(true);
    }
  }, [debugParam, setDebugEnabled]);

  useEffect(() => {
    setTourId(getCurrentTourId() ?? null);
  }, [segments]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const formatError = (err: unknown) => {
      if (err instanceof Error) {
        return { message: err.message, stack: err.stack };
      }
      if (typeof err === 'string') {
        return { message: err };
      }
      try {
        return { message: JSON.stringify(err) };
      } catch {
        return { message: String(err) };
      }
    };

    const handleError = (event: ErrorEvent) => {
      const details = formatError(event.error ?? event.message);
      logToBff(
        'frontend.unhandled.error',
        {
          ...details,
          source: event.filename,
          line: event.lineno,
          column: event.colno,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const details = formatError(event.reason);
      logToBff(
        'frontend.unhandled.rejection',
        {
          ...details,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [logToBff, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.console || typeof window.console.error !== 'function') {
      return;
    }

    const originalConsoleError = window.console.error.bind(window.console);

    const stringifyConsoleValue = (value: unknown): string => {
      if (value instanceof Error) {
        return `${value.name}: ${value.message}`;
      }
      if (typeof value === 'string') {
        return value;
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    };

    const patchedConsoleError: typeof console.error = (...args: unknown[]) => {
      originalConsoleError(...args);

      try {
        const firstError = args.find((value): value is Error => value instanceof Error);
        logToBff(
          'frontend.console.error',
          {
            summary: args.map(stringifyConsoleValue).join(' | '),
            errorName: firstError?.name,
            errorMessage: firstError?.message,
            stack: firstError?.stack,
            argumentCount: args.length,
            userId: user?.id,
            path: window.location?.pathname ?? '',
            search: window.location?.search ?? '',
            href: window.location?.href ?? '',
            userAgent: navigator?.userAgent ?? '',
          },
          'error'
        );
      } catch {
        // Keep console behavior intact even if forwarding fails.
      }
    };

    window.console.error = patchedConsoleError;
    return () => {
      if (window.console.error === patchedConsoleError) {
        window.console.error = originalConsoleError;
      }
    };
  }, [logToBff, user?.id]);

  useEffect(() => {
    if (!roundId || !user?.token) {
      setMysteryMenuEnabled(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [roundRes, defsRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          fetch(`${apiBaseUrl}/api/game-definitions`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
        ]);
        if (!roundRes.ok || !defsRes.ok) {
          if (!cancelled) setMysteryMenuEnabled(false);
          return;
        }
        const roundPayload = (await roundRes.json()) as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
        };
        const defsPayload = (await defsRes.json()) as Array<{ id?: string; name?: string }>;
        const ids = roundPayload.gameDefinitionIds ?? roundPayload.GameDefinitionIds ?? [];
        const selected = new Set(ids);
        const hasMystery = defsPayload.some(
          (def) =>
            def?.id &&
            selected.has(def.id) &&
            typeof def.name === 'string' &&
            def.name.toLowerCase() === 'mystery'
        );
        if (!cancelled) setMysteryMenuEnabled(hasMystery);
      } catch {
        if (!cancelled) setMysteryMenuEnabled(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, roundId, user?.token]);

  useEffect(() => {
    if (!debugEnabled || !user?.token) {
      setDebugTourName(null);
      setDebugRoundName(null);
      setDebugPlayerId(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setDebugLoading(true);
      try {
        const requests: Array<Promise<Response>> = [];
        if (user.id) {
          requests.push(
            fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (tourId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (roundId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        const responses = await Promise.all(requests);
        let resolvedTourName: string | null = null;
        let resolvedRoundName: string | null = null;
        let resolvedPlayerId: string | null = null;
        for (const response of responses) {
          if (!response.ok) continue;
          const payload = (await response.json()) as { name?: string; tourName?: string; courseName?: string; id?: string };
          if (payload.id && !resolvedPlayerId) {
            resolvedPlayerId = payload.id;
          }
          if (payload.tourName || payload.name) {
            resolvedTourName = payload.tourName ?? payload.name ?? null;
          }
          if (payload.courseName || payload.name) {
            resolvedRoundName = payload.courseName ?? payload.name ?? null;
          }
        }
        if (!cancelled) {
          setDebugTourName(resolvedTourName);
          setDebugRoundName(resolvedRoundName);
          setDebugPlayerId(resolvedPlayerId);
        }
      } catch {
        if (!cancelled) {
          setDebugTourName(null);
          setDebugRoundName(null);
          setDebugPlayerId(null);
        }
      } finally {
        if (!cancelled) setDebugLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debugEnabled, roundId, tourId, user?.id, user?.token]);

  useEffect(() => {
    if (!debugOpen) return;
    let cancelled = false;
    const loadCoachMarks = async () => {
      setDebugCoachMarksLoading(true);
      try {
        const states = await getCoachMarkStatesForDebug(apiBaseUrl);
        if (!cancelled) {
          setDebugCoachMarks(
            states.map((state) => ({
              key: state.key,
              shownCount: state.shownCount,
              maxShows: state.maxShows,
            }))
          );
        }
      } catch {
        if (!cancelled) {
          setDebugCoachMarks([]);
        }
      } finally {
        if (!cancelled) {
          setDebugCoachMarksLoading(false);
        }
      }
    };
    void loadCoachMarks();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debugOpen]);

  const buildRequestId = useCallback(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
    return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }, []);

  const buildGuid = useCallback(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const random = `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`.padEnd(32, '0').slice(0, 32);
    return `${random.slice(0, 8)}-${random.slice(8, 12)}-${random.slice(12, 16)}-${random.slice(16, 20)}-${random.slice(20, 32)}`;
  }, []);

  const clearBugImage = useCallback(() => {
    setBugReportImage((prev) => {
      if (prev?.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  }, []);

  const selectBugImageFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        setBugReportError(t('menu.reportBugInvalidImage'));
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        setBugReportError(t('menu.reportBugImageTooLarge'));
        return;
      }
      if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
        setBugReportError(t('menu.reportBugFileUnsupported'));
        return;
      }
      setBugReportError(null);
      setBugReportImage((prev) => {
        if (prev?.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(prev.previewUrl);
        }
        return {
          file,
          fileName: file.name || 'bug-report-image.png',
          contentType: file.type || 'image/png',
          size: file.size,
          previewUrl: URL.createObjectURL(file),
          uploadedUrl: null,
        };
      });
    },
    [t]
  );

  const requestBugImagePresign = useCallback(
    async (file: File): Promise<{ uploadUrl: string; publicUrl: string }> => {
      const ws = getSharedBffWebSocket(apiBaseUrl);
      ws.setAuthToken(user?.token ?? null);
      const requestId = buildGuid();
      return await new Promise((resolve, reject) => {
        let done = false;
        const finish = (fn: () => void) => {
          if (done) return;
          done = true;
          fn();
        };
        const timeout = setTimeout(() => {
          finish(() => {
            unsubscribe();
            reject(new Error('bug.image.presign.timeout'));
          });
        }, 12000);
        const unsubscribe = ws.onMessage((message: BffInboundMessage) => {
          if ((message as { requestId?: string }).requestId !== requestId) return;
          if (message.type === 'bug.image.presign.ack') {
            const payload = message as { uploadUrl?: string; publicUrl?: string };
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              if (!payload.uploadUrl || !payload.publicUrl) {
                reject(new Error('bug.image.presign.invalidAck'));
                return;
              }
              resolve({ uploadUrl: payload.uploadUrl, publicUrl: payload.publicUrl });
            });
          } else if (message.type === 'bug.image.presign.error') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error((message as { message?: string }).message ?? 'bug.image.presign.failed'));
            });
          }
        });
        const sent = ws.sendBugImagePresign({
          requestId,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        });
        if (!sent) {
          finish(() => {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error('bug.image.presign.sendFailed'));
          });
        }
      });
    },
    [apiBaseUrl, buildGuid, user?.token]
  );

  const ensureBugImageUploaded = useCallback(async () => {
    if (!bugReportImage) {
      return null;
    }
    if (bugReportImage.uploadedUrl) {
      return bugReportImage.uploadedUrl;
    }
    const { uploadUrl, publicUrl } = await requestBugImagePresign(bugReportImage.file);
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': bugReportImage.contentType || 'image/png' },
      body: bugReportImage.file,
    });
    if (!uploadResponse.ok) {
      throw new Error(`bug.image.upload.failed.${uploadResponse.status}`);
    }
    setBugReportImage((prev) => (prev ? { ...prev, uploadedUrl: publicUrl } : prev));
    return publicUrl;
  }, [bugReportImage, requestBugImagePresign]);

  const loadBugReportRoundContext = useCallback(async () => {
    if (!effectiveRoundId || !user?.token) {
      return undefined;
    }

    const headers = { Authorization: `Bearer ${user.token}` };
    const fetchJsonSafe = async (url: string): Promise<unknown> => {
      try {
        const response = await fetch(url, { headers });
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    };

    const [roundPayload, playersPayload, scoreboardPayload, holeEntriesPayload, holeEventsPayload, gameDefinitionsPayload, tourLeaderboardPayload] =
      await Promise.all([
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/players`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/scoreboard`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-entries`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-events`),
        fetchJsonSafe(`${apiBaseUrl}/api/game-definitions`),
        tourId ? fetchJsonSafe(`${apiBaseUrl}/api/tours/${tourId}/leaderboard`) : Promise.resolve(null),
      ]);

    const round = (roundPayload ?? {}) as {
      gameDefinitionIds?: string[];
      GameDefinitionIds?: string[];
      status?: string;
      Status?: string;
      name?: string;
      Name?: string;
      courseName?: string;
      CourseName?: string;
    };
    const gameDefinitionIds = round.gameDefinitionIds ?? round.GameDefinitionIds ?? [];

    const gameDefinitions = Array.isArray(gameDefinitionsPayload)
      ? (gameDefinitionsPayload as Array<{ id?: string; name?: string; competitionType?: string }>)
          .filter((item) => !!item?.id && gameDefinitionIds.includes(item.id as string))
          .map((item) => ({
            id: item.id,
            name: item.name,
            competitionType: item.competitionType,
          }))
      : [];

    const playersRaw = Array.isArray((playersPayload as { players?: unknown[] })?.players)
      ? ((playersPayload as { players?: unknown[] }).players as Array<{ id?: string; name?: string }>)
      : Array.isArray(playersPayload)
        ? (playersPayload as Array<{ id?: string; name?: string }>)
        : [];
    const players = playersRaw.map((player) => ({ id: player.id, name: player.name }));

    const holeEntries = Array.isArray(holeEntriesPayload) ? (holeEntriesPayload as Array<Record<string, unknown>>) : [];
    const holeScoringByHole = Object.values(
      holeEntries.reduce<Record<string, { hole: number; entries: Array<Record<string, unknown>> }>>((acc, entry) => {
        const hole =
          (typeof entry.hole === 'number' && Number.isFinite(entry.hole) ? entry.hole : undefined) ??
          (typeof entry.Hole === 'number' && Number.isFinite(entry.Hole) ? entry.Hole : undefined) ??
          (typeof entry.holeNumber === 'number' && Number.isFinite(entry.holeNumber) ? entry.holeNumber : undefined) ??
          (typeof entry.HoleNumber === 'number' && Number.isFinite(entry.HoleNumber) ? entry.HoleNumber : undefined);
        if (!hole) return acc;
        const key = String(hole);
        if (!acc[key]) {
          acc[key] = { hole, entries: [] };
        }
        acc[key].entries.push({
          playerId: entry.playerId ?? entry.PlayerId ?? null,
          playerName: entry.playerName ?? entry.PlayerName ?? null,
          grossStrokes: entry.grossStrokes ?? entry.GrossStrokes ?? entry.grossStroke ?? entry.GrossStroke ?? null,
          pickup: entry.pickup ?? entry.Pickup ?? null,
          scorePoints: entry.scorePoints ?? entry.ScorePoints ?? null,
          roundGamePoints: entry.roundGamePoints ?? entry.RoundGamePoints ?? null,
        });
        return acc;
      }, {})
    ).sort((a, b) => a.hole - b.hole);

    const gameResults =
      (scoreboardPayload as { scores?: unknown[]; Scores?: unknown[] } | null)?.scores ??
      (scoreboardPayload as { scores?: unknown[]; Scores?: unknown[] } | null)?.Scores ??
      (Array.isArray(scoreboardPayload) ? scoreboardPayload : null);

    return {
      roundId: effectiveRoundId,
      roundName: round.name ?? round.Name ?? null,
      courseName: round.courseName ?? round.CourseName ?? null,
      roundStatus: round.status ?? round.Status ?? null,
      playerCount: players.length,
      players,
      gamesIncluded: {
        gameDefinitionIds,
        definitions: gameDefinitions,
      },
      leaderboard: tourLeaderboardPayload ?? undefined,
      gameResults: gameResults ?? undefined,
      scoringPerHole: holeScoringByHole,
      holeEvents: Array.isArray(holeEventsPayload) ? holeEventsPayload : undefined,
    };
  }, [apiBaseUrl, effectiveRoundId, tourId, user?.token]);

  const submitBugReport = useCallback(async () => {
    const description = bugReportDescription.trim();
    if (!description && !bugReportImage) {
      setBugReportError(t('menu.reportBugMissingContent'));
      return;
    }
    setBugReportSubmitting(true);
    setBugReportOpen(false);
    setBugReportError(null);
    try {
      const ws = getSharedBffWebSocket(apiBaseUrl);
      ws.setAuthToken(user?.token ?? null);
      const requestId = buildGuid();
      const imageUrl = await ensureBugImageUploaded();
      const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
      let roundContext: unknown;
      try {
        roundContext = await loadBugReportRoundContext();
      } catch (error) {
        roundContext = {
          error: error instanceof Error ? error.message : String(error),
        };
      }
      const context = {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        search: typeof window !== 'undefined' ? window.location.search : '',
        roundId: effectiveRoundId ?? undefined,
        tourId: tourId ?? undefined,
        debugEnabled,
        platform: Platform.OS,
        viewport:
          typeof window !== 'undefined'
            ? { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio }
            : undefined,
        roundContext,
      };

      await new Promise<void>((resolve, reject) => {
        let done = false;
        const finish = (fn: () => void) => {
          if (done) return;
          done = true;
          fn();
        };
        const timeout = setTimeout(() => {
          finish(() => {
            unsubscribe();
            reject(new Error('bug.report.timeout'));
          });
        }, 15000);
        const unsubscribe = ws.onMessage((message: BffInboundMessage) => {
          if ((message as { requestId?: string }).requestId !== requestId) return;
          if (message.type === 'bug.report.ack') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              resolve();
            });
          } else if (message.type === 'bug.report.error') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error((message as { message?: string }).message ?? 'bug.report.failed'));
            });
          }
        });
        const sent = ws.sendBugReport({
          requestId,
          description: description || undefined,
          imageUrl: imageUrl ?? undefined,
          pageUrl,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          appVersion: typeof process !== 'undefined' ? process.env.EXPO_PUBLIC_APP_VERSION : undefined,
          timestamp: new Date().toISOString(),
          context,
        });
        if (!sent) {
          finish(() => {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error('bug.report.sendFailed'));
          });
        }
      });

      setBugReportDescription('');
      clearBugImage();
    } catch (error) {
      setBugReportError(
        `${t('menu.reportBugSendFailed')}: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setBugReportSubmitting(false);
    }
  }, [
    apiBaseUrl,
    bugReportDescription,
    bugReportImage,
    buildGuid,
    clearBugImage,
    debugEnabled,
    effectiveRoundId,
    ensureBugImageUploaded,
    loadBugReportRoundContext,
    t,
    tourId,
    user?.token,
  ]);

  const openBugFilePicker = useCallback(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setBugReportError(t('menu.reportBugFileUnsupported'));
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      selectBugImageFile(file);
    };
    input.click();
  }, [selectBugImageFile, t]);

  const bugReportImageMeta = useMemo(() => {
    if (!bugReportImage) {
      return '';
    }
    return `${bugReportImage.fileName} (${Math.round(bugReportImage.size / 1024)} KB)`;
  }, [bugReportImage]);

  useUserInfoSync({ apiBaseUrl, user, setUser, buildRequestId });

  useEffect(() => {
    if ((isPublicInviteView || isPublicLandingView || topSegment === 'login' || topSegment === 'register') && leftMenuOpen) {
      setLeftMenuOpen(false);
    }
  }, [isPublicInviteView, isPublicLandingView, leftMenuOpen, topSegment]);

  useEffect(() => {
    if (!bugReportOpen) return;
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items || items.length === 0) {
        return;
      }
      for (const item of Array.from(items)) {
        if (!item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (!file) continue;
        event.preventDefault();
        selectBugImageFile(file);
        break;
      }
    };

    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('paste', onPaste);
    };
  }, [bugReportOpen, selectBugImageFile]);

  useEffect(() => {
    return () => {
      clearBugImage();
    };
  }, [clearBugImage]);

  const handleResetCoachMarks = useCallback(async () => {
    await resetCoachMarksForDebug(apiBaseUrl);
    const states = await getCoachMarkStatesForDebug(apiBaseUrl);
    setDebugCoachMarks(
      states.map((state) => ({
        key: state.key,
        shownCount: state.shownCount,
        maxShows: state.maxShows,
      }))
    );
  }, [apiBaseUrl]);

  if (!authChecked) {
    return null;
  }

  const showBottomMenu =
    topSegment !== 'login' &&
    topSegment !== 'register' &&
    !isPublicInviteView &&
    !isPublicLandingView;
  const selectPlayTourId = getCurrentTourId();
  const selectPlayRoundId = effectiveRoundId;
  const canOpenSelectPlay = !!selectPlayRoundId && !scorerOnlyMode;
  const selectPlayPath = selectPlayRoundId
    ? `/round/${selectPlayRoundId}/select-games${isHoleRoute ? '?source=hole' : ''}`
    : selectPlayTourId
      ? `/tour/${selectPlayTourId}/round-init`
      : '/tournaments';
  const canOpenRoundSettings = !!effectiveRoundId && !scorerOnlyMode;
  const canOpenConfigCourse = !!effectiveRoundId && !scorerOnlyMode;
  const configCoursePath = effectiveRoundId
    ? `/config-course?roundId=${encodeURIComponent(effectiveRoundId)}`
    : '/config-course';
  const roundSettingsPath = effectiveRoundId
    ? `/round/${effectiveRoundId}/roundstart?mode=update${debugEnabled ? '&debug=true' : ''}`
    : '/tournaments';
  const isMobileView = Platform.OS !== 'web' || compactWebHeader;
  const tourPath = tourId
    ? isMobileView
      ? `/my-tours${debugEnabled ? '?debug=true' : ''}`
      : `/tour/${tourId}/edit${debugEnabled ? '?debug=true' : ''}`
    : `/my-tours${debugEnabled ? '?debug=true' : ''}`;

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <View style={styles.shell}>
        <View style={styles.stack}>
          <ServiceStatusModal
            open={serviceStatusOpen}
            onClose={() => setServiceStatusOpen(false)}
            missing={serviceStatusMissing}
            checkedAt={serviceStatusCheckedAt}
            hasWarnings={serviceStatusHasWarnings}
            warnings={serviceStatusWarnings}
          />
          <DebugModal
            open={debugOpen}
            onClose={() => setDebugOpen(false)}
            user={user}
            playerId={debugPlayerId}
            roundId={roundId}
            tourId={tourId}
            roundName={debugRoundName}
            tourName={debugTourName}
            loading={debugLoading}
            coachMarks={debugCoachMarks}
            coachMarksLoading={debugCoachMarksLoading}
            onDisable={() => {
              setDebugEnabled(false);
              setDebugOpen(false);
            }}
            onResetCoachMarks={handleResetCoachMarks}
          />
          <Modal
            transparent
            visible={!isPublicInviteView && leftMenuOpen}
            animationType="fade"
            onRequestClose={() => setLeftMenuOpen(false)}
          >
            <LeftMenu
              open={leftMenuOpen}
              canOpenSelectPlay={canOpenSelectPlay}
              selectPlayPath={selectPlayPath}
              isHoleRoute={isHoleRoute}
              canOpenRoundSettings={canOpenRoundSettings}
              roundSettingsPath={roundSettingsPath}
              canOpenConfigCourse={canOpenConfigCourse}
              scorerOnlyMode={scorerOnlyMode}
              configCoursePath={configCoursePath}
              mysteryEnabled={mysteryMenuEnabled}
              mysteryPath={roundId ? `/round/${roundId}/mystery-prize` : '/round'}
              tourPath={tourPath}
              onOpenBugReport={() => {
                setLeftMenuOpen(false);
                setBugReportError(null);
                setBugReportOpen(true);
              }}
              onClose={() => setLeftMenuOpen(false)}
            />
          </Modal>
          <Modal
            transparent
            visible={bugReportOpen}
            animationType="fade"
            onRequestClose={() => setBugReportOpen(false)}
          >
            <Pressable style={styles.leftMenuBackdrop} onPress={() => setBugReportOpen(false)}>
              <Pressable style={styles.bugModalCard} onPress={() => null}>
                <Text style={styles.bugModalTitle}>{t('menu.reportBugTitle')}</Text>
                <Text style={styles.bugModalHint}>{t('menu.reportBugHint')}</Text>
                <TextInput
                  value={bugReportDescription}
                  onChangeText={setBugReportDescription}
                  multiline
                  numberOfLines={5}
                  style={styles.bugModalInput}
                  placeholder={t('menu.reportBugDescriptionPlaceholder')}
                  placeholderTextColor={appColors.text.muted}
                  editable={!bugReportSubmitting}
                />
                <View style={styles.bugModalRow}>
                  <Pressable
                    style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={openBugFilePicker}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{t('menu.reportBugAddImage')}</Text>
                  </Pressable>
                  {bugReportImage ? (
                    <Pressable
                      style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                      onPress={clearBugImage}
                      disabled={bugReportSubmitting}
                    >
                      <Text style={styles.bugModalButtonText}>{t('menu.reportBugRemoveImage')}</Text>
                    </Pressable>
                  ) : null}
                </View>
                {bugReportImage ? (
                  <View style={styles.bugPreviewCard}>
                    <Image source={{ uri: bugReportImage.previewUrl }} style={styles.bugPreviewImage} resizeMode="cover" />
                    <Text style={styles.bugPreviewMeta}>{bugReportImageMeta}</Text>
                  </View>
                ) : (
                  <Text style={styles.bugModalHint}>{t('menu.reportBugPasteHint')}</Text>
                )}
                {bugReportError ? <Text style={styles.bugModalError}>{bugReportError}</Text> : null}
                <View style={styles.bugModalRow}>
                  <Pressable
                    style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={() => setBugReportOpen(false)}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{t('menu.close')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.bugModalButtonPrimary, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={submitBugReport}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{bugReportSubmitting ? t('menu.reportBugSending') : t('menu.reportBugSend')}</Text>
                  </Pressable>
                </View>
              </Pressable>
            </Pressable>
          </Modal>
          <Stack
            screenOptions={{
              headerStyle: {
                backgroundColor: appColors.background.app,
              },
              headerShadowVisible: false,
              headerTitleAlign: 'center',
              headerTintColor: appColors.text.heading,
              headerTitleStyle: {
                color: appColors.text.heading,
                fontSize: 30,
                fontWeight: '700',
                letterSpacing: 0.2,
                ...Platform.select({
                  web: { fontFamily: 'Newsreader, Playfair Display, Georgia, serif' },
                  default: { fontFamily: 'serif' },
                }),
              },
              contentStyle: {
                backgroundColor: appColors.background.app,
              },
              headerLeft: () =>
                isPublicInviteView || scorerOnlyMode || isAnonymousMode ? null : (
                  <Pressable style={styles.headerIconButton} onPress={() => setLeftMenuOpen((prev) => !prev)}>
                    <Image source={faviconImage} style={styles.headerIcon} />
                  </Pressable>
                ),
              headerRight: () => (
                isPublicInviteView ? (
                  <Pressable
                    style={[styles.loginButton, compactWebHeader && styles.loginButtonCompact]}
                    onPress={() => {
                      void handleRedirect();
                    }}
                    accessibilityLabel={t('auth.loginTitle')}
                  >
                    <Text style={styles.loginButtonText}>{t('auth.loginTitle')}</Text>
                  </Pressable>
                ) : (
                  <View style={[styles.headerRight, compactWebHeader && styles.headerRightCompact]}>
                    {debugEnabled && !compactWebHeader ? (
                      <Pressable
                        style={styles.debugButton}
                        onPress={() => setDebugOpen(true)}
                        accessibilityLabel={t('debug.buttonLabel')}
                      >
                        <Text style={styles.debugButtonText}>{t('debug.button')}</Text>
                      </Pressable>
                    ) : null}
                    {canCheckServiceHealth && !compactWebHeader ? (
                      serviceStatusMissing.length > 0 || serviceStatusFetchFailed ? (
                        <Pressable
                          style={[
                            styles.serviceStatusButtonCritical,
                            serviceStatusFetchFailed && !serviceStatusFlashOn ? styles.serviceStatusButtonCriticalDim : null,
                          ]}
                          onPress={() => setServiceStatusOpen(true)}
                          accessibilityLabel={t('serviceStatus.buttonLabel')}
                        >
                          <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                        </Pressable>
                      ) : serviceStatusHasWarnings ? (
                        <Pressable
                          style={styles.serviceStatusButtonWarning}
                          onPress={() => setServiceStatusOpen(true)}
                          accessibilityLabel={t('serviceStatus.buttonLabel')}
                        >
                          <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                        </Pressable>
                      ) : null
                    ) : null}
                    <HeaderUser
                      compact={compactWebHeader}
                      hideProfileAndFriends={isAnonymousMode}
                      onOpenBugReport={() => {
                        setBugReportError(null);
                        setBugReportOpen(true);
                      }}
                    />
                  </View>
                )
              ),
            }}
          >
            <Stack.Screen name="index" options={{ href: null, headerShown: false }} />
            <Stack.Screen name="login" options={{ title: t('auth.loginTitle'), headerShown: false }} />
            <Stack.Screen name="register" options={{ title: t('register.title'), headerShown: false }} />
            <Stack.Screen name="welcome" options={{ title: t('welcome.title') }} />
            <Stack.Screen name="tournaments" options={{ title: t('tournaments.title') }} />
            <Stack.Screen
              name="my-tours"
              options={{
                title: t('welcome.myToursNav'),
                headerTitleContainerStyle: {
                  left: 5,
                },
              }}
            />
            <Stack.Screen name="friends" options={{ title: t('friends.title') }} />
            <Stack.Screen name="modal" options={{ presentation: 'modal', title: t('modal.title') }} />
            <Stack.Screen name="debug/deeplink" options={{ title: t('debug.deeplinkTitle') }} />
            <Stack.Screen name="round/[roundId]/select-games" options={{ title: t('selectGames.title') }} />
            <Stack.Screen name="round/[roundId]/roundstart" options={{ title: t('roundStartSetup.title') }} />
            <Stack.Screen name="round/[roundId]/mystery-prize" options={{ title: t('mysteryPrize.title') }} />
            <Stack.Screen name="round/[roundId]/hole/[hole]/index" options={{ title: t('hole.title') }} />
            <Stack.Screen name="round/[roundId]/hole/[hole]/compact" options={{ title: t('hole.title'), animation: 'slide_from_right' }} />
            <Stack.Screen name="tour/[tourId]/leaderboard" options={{ title: t('tourLeaderboard.title') }} />
            <Stack.Screen name="tour/invite/[inviteToken]" options={{ title: t('tourLeaderboard.title') }} />
            <Stack.Screen name="round/[roundId]/summary" options={{ title: t('roundResults.title') }} />
            <Stack.Screen name="tour/[tourId]/round-init" options={{ title: t('players.roundInitTitle') }} />
            <Stack.Screen name="round/[roundId]/scorecard" options={{ title: t('scorecard.title') }} />
            <Stack.Screen
              name="sheet/tour/[tourId]/leaderboard"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen
              name="sheet/round/[roundId]/summary"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen
              name="sheet/round/[roundId]/scorecard"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen name="tour/[tourId]/edit" options={{ headerShown: false }} />
            <Stack.Screen name="profile/index" options={{ title: t('profile.title') }} />
            <Stack.Screen name="config-course" options={{ title: t('courseConfig.title'), presentation: 'modal' }} />
          </Stack>
        </View>
        {showBottomMenu ? <BottomMenu /> : null}
      </View>
    </ThemeProvider>
  );
}

type LeftMenuProps = {
  open: boolean;
  canOpenSelectPlay: boolean;
  selectPlayPath: string;
  isHoleRoute: boolean;
  canOpenRoundSettings: boolean;
  roundSettingsPath: string;
  canOpenConfigCourse: boolean;
  scorerOnlyMode: boolean;
  configCoursePath: string;
  mysteryEnabled: boolean;
  mysteryPath: string;
  tourPath: string;
  onClose: () => void;
};

const LeftMenu = ({
  open,
  canOpenSelectPlay,
  selectPlayPath,
  isHoleRoute,
  canOpenRoundSettings,
  roundSettingsPath,
  canOpenConfigCourse,
  scorerOnlyMode,
  configCoursePath,
  mysteryEnabled,
  mysteryPath,
  tourPath,
  onClose,
}: LeftMenuProps) => {
  const router = useRouter();
  const { t } = useI18n();

  if (!open) return null;

  return (
    <Pressable style={styles.leftMenuBackdrop} onPress={onClose}>
      <View style={styles.leftMenuCard}>
        <Pressable
          style={styles.leftMenuItem}
          onPress={() => {
            onClose();
            router.push('/welcome');
          }}
        >
          <Text style={styles.leftMenuItemText}>{t('menu.home')}</Text>
        </Pressable>
        <Pressable
          style={[styles.leftMenuItem, !canOpenSelectPlay && styles.leftMenuItemDisabled]}
          disabled={!canOpenSelectPlay}
          onPress={() => {
            onClose();
            router.push(selectPlayPath);
          }}
        >
          <Text style={[styles.leftMenuItemText, !canOpenSelectPlay && styles.leftMenuItemTextDisabled]}>
            {t('menu.selectGames')}
          </Text>
        </Pressable>
        <Pressable
          style={[styles.leftMenuItem, !canOpenRoundSettings && styles.leftMenuItemDisabled]}
          disabled={!canOpenRoundSettings}
          onPress={() => {
            onClose();
            router.push(roundSettingsPath);
          }}
        >
          <Text style={[styles.leftMenuItemText, !canOpenRoundSettings && styles.leftMenuItemTextDisabled]}>
            {t('tournaments.roundSettings')}
          </Text>
        </Pressable>
        {mysteryEnabled ? (
          <Pressable
            style={styles.leftMenuItem}
            onPress={() => {
              onClose();
              router.push(mysteryPath);
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('mysteryPrize.title')}</Text>
          </Pressable>
        ) : null}
        <Pressable
          style={[styles.leftMenuItem, scorerOnlyMode && styles.leftMenuItemDisabled]}
          disabled={scorerOnlyMode}
          onPress={() => {
            onClose();
            router.push(tourPath);
          }}
        >
          <Text style={[styles.leftMenuItemText, scorerOnlyMode && styles.leftMenuItemTextDisabled]}>{t('menu.tour')}</Text>
        </Pressable>
        <Pressable
          style={[styles.leftMenuItem, !canOpenConfigCourse && styles.leftMenuItemDisabled]}
          disabled={!canOpenConfigCourse}
          onPress={() => {
            onClose();
            router.push(configCoursePath);
          }}
        >
          <Text style={[styles.leftMenuItemText, !canOpenConfigCourse && styles.leftMenuItemTextDisabled]}>
            {t('menu.configCourse')}
          </Text>
        </Pressable>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  stack: {
    flex: 1,
  },
  headerIcon: {
    width: 40,
    height: 40,
    marginLeft: 12,
  },
  headerIconButton: {
    padding: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  headerRightCompact: {
    gap: 4,
  },
  loginButton: {
    marginRight: 8,
    backgroundColor: appColors.surface.alt,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.default,
    flexShrink: 1,
  },
  loginButtonCompact: {
    marginRight: 0,
    paddingHorizontal: 10,
  },
  loginButtonText: {
    color: appColors.text.secondary,
    fontWeight: '800',
    fontSize: 12,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  serviceStatusButtonCritical: {
    backgroundColor: '#d7263d',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#f08a95',
  },
  serviceStatusButtonCriticalDim: {
    opacity: 0.35,
  },
  serviceStatusButtonWarning: {
    backgroundColor: '#e68a00',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#d1d27a',
  },
  serviceStatusButtonText: {
    color: '#d8f3f4',
    fontWeight: '700',
  },
  debugButton: {
    backgroundColor: appColors.surface.alt,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.default,
  },
  debugButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  leftMenuBackdrop: {
    flex: 1,
    backgroundColor: appColors.accent.overlay,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      },
    }),
  },
  leftMenuCard: {
    position: 'absolute',
    top: 52,
    left: 12,
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    minWidth: 170,
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
  leftMenuItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  leftMenuItemDisabled: {
    opacity: 0.45,
  },
  leftMenuItemText: {
    color: appColors.text.primary,
    fontWeight: '600',
    fontSize: 14,
  },
  leftMenuItemTextDisabled: {
    color: appColors.text.muted,
  },
  bugModalCard: {
    position: 'absolute',
    top: 80,
    left: 16,
    right: 16,
    maxHeight: '82%',
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    padding: 14,
    gap: 10,
  },
  bugModalTitle: {
    color: appColors.text.heading,
    fontSize: 18,
    fontWeight: '800',
  },
  bugModalHint: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  bugModalInput: {
    minHeight: 110,
    backgroundColor: appColors.background.panelAlt,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    color: appColors.text.primary,
    paddingHorizontal: 10,
    paddingVertical: 8,
    textAlignVertical: 'top',
  },
  bugModalRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  bugModalButton: {
    backgroundColor: appColors.background.interactiveMuted,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  bugModalButtonPrimary: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.accent.positive,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 'auto',
  },
  bugModalButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  bugPreviewCard: {
    borderRadius: 12,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    backgroundColor: appColors.background.panelAlt,
    padding: 8,
    gap: 6,
  },
  bugPreviewImage: {
    width: '100%',
    height: 180,
    borderRadius: 8,
    backgroundColor: appColors.background.app,
  },
  bugPreviewMeta: {
    color: appColors.text.secondary,
    fontSize: 11,
  },
  bugModalError: {
    color: '#ff9ca8',
    fontSize: 12,
    fontWeight: '600',
  },
});
